import moment from 'moment-timezone';
import {
    getCourtSchedulerConfigForTheDay,
    getDefaultCourtSchedulerConfig,
    saveCourtSchedulerConfig,
    getUnscheduledMatchesForDrawV3,
    getScheduledMatchesForEventV3,
    scheduleMatchForEvent,
    revertScheduleForEvent,
    publishScheduleForEvent,
    deScheduleMatches,
    updateMatchSettings,
    startMatches,
    pauseMatches,
    restartMatch,
    getDrawMatchCounts,
    adjustMatches,
} from '../api/tmsEventsCourtSchedulerApi';
import {
    GET_COURT_CONFIG_DATA,
    GET_COURT_CONFIG_DATA_SUCCESS,
    GET_COURT_CONFIG_DATA_FAILURE,
    SAVE_COURT_CONFIG_DATA,
    SAVE_COURT_CONFIG_DATA_SUCCESS,
    SAVE_COURT_CONFIG_DATA_FAILURE,
    GET_DEFAULT_COURT_CONFIG_DATA,
    GET_DEFAULT_COURT_CONFIG_DATA_SUCCESS,
    GET_DEFAULT_COURT_CONFIG_DATA_FAILURE,
    UPDATE_FACILITIES_BASED_ON_CONFIG,
    UPDATE_SHOW_CONFIG_MODAL,
    UPDATE_SELECTED_FACILITY,
    UPDATE_SELECTED_DRAW_WITH_MATCHES,
    UPDATE_DRAWS_WITH_SCHEDULED_MATCHES,
    GET_UNSCHEDULED_DRAW_MATCHES,
    GET_UNSCHEDULED_DRAW_MATCHES_FAILURE,
    GET_UNSCHEDULED_DRAW_MATCHES_SUCCESS,
    UPDATE_DRAWS_LIST,
    UPDATE_DRAGGING_MATCH,
    UPDATE_SCHEDULED_MATCHES,
    UPDATE_HAS_TIME_CONFLICTS,
    GET_SCHEDULED_MATCHES,
    GET_SCHEDULED_MATCHES_FAILURE,
    GET_SCHEDULED_MATCHES_SUCCESS,
    SAVE_SCHEDULE_MATCH,
    SAVE_SCHEDULE_MATCH_SUCCESS,
    PUBLISH_SCHEDULED_MATCHES,
    PUBLISH_SCHEDULED_MATCHES_SUCCESS,
    PUBLISH_SCHEDULED_MATCHES_FAILURE,
    REVERT_SCHEDULED_MATCHES,
    REVERT_SCHEDULED_MATCHES_FAILURE,
    REVERT_SCHEDULED_MATCHES_SUCCESS,
    MATCH_SETTINGS_UPDATE,
    MATCH_SETTINGS_UPDATE_FAILURE,
    SAVE_SCHEDULE_MATCH_FAILURE,
    MATCH_SETTINGS_UPDATE_SUCCESS,
    UPDATE_SELECTED_MATCH_IDS,
    DE_SCHEDULE_MATCHES,
    DE_SCHEDULE_MATCHES_FAILURE,
    DE_SCHEDULE_MATCHES_SUCCESS,
    CLEAR_COURT_SCHEDULAR_DATA,
    CLOSE_DRAWER,
    UPDATE_DRAW_MATCH_COUNT,
    ADJUST_SELECTED_MATCHES,
    ADJUST_SELECTED_MATCHES_SUCCESS,
    ADJUST_SELECTED_MATCHES_FAILURE,
} from './actionTypes/court-scheduler-types';
import {
    addOrUpdateTimePreferenceToScheduledMatch,
    clampMatchEndTimeToDayEnd,
    CONFLICT_TITLE_RESCHEDULE,
    CONFLICT_TITLE_SCHEDULE,
    dateFormat,
    checkMatchTimeConflicts,
    generateRandomColor,
    getActiveFacilitiesAndCourts,
    getDrawName,
    getFacilitiesAndCourtsToDisplay,
    getDrawData,
    getScheduledMatchObjectFromDraftOpp,
    createScheduledMatchObjectFromUnscheduledMatch,
    getUnScheduledMatchFromDrawMatch,
    resetColorIndex,
    shouldDisplayMainDrawAsChildDraw,
    updateScheduledMatchObjectTimes,
    dateFormatForDayConfig,
} from '../utils/courtSchedular';
import { getApiErrorMessage } from '../utils/apiError';
import { updateDraw, updateProDraw } from '../api/tmsEventsApi';
import { getDraws, getOrderOfPlay, getOrderOfPlayMatches } from './draw-edit-actions';
import * as nextAvailableActions from './next-available-actions';
import { createTypeByIdDict, updateCategoryTypesName } from '../utils/formatting';
import { shouldDisplayDraws } from '../utils/events';
import { drawEliminationTypeIds, drawScheduleTypeIds } from '../utils/eventConstants';
import { showErrorPopup } from './notification-actions';

const buildMatchCountsDict = (matchCounts) => matchCounts.reduce((dict, item) => {
        dict[item.drawId] = {
            totalMatchCount: item.totalMatchCount,
            scheduledMatchCount: item.scheduledMatchCount,
        };
        return dict;
    }, {});

export const getDefaultConfigData = (eventId) => async (dispatch, getState, { mainApi }) => {
    dispatch({
        type: GET_DEFAULT_COURT_CONFIG_DATA,
    });
    try {
        const response = await getDefaultCourtSchedulerConfig(mainApi, eventId);
        const { data, status } = response;
        if (status === 204) {
            dispatch({ type: GET_DEFAULT_COURT_CONFIG_DATA_FAILURE });
        } else {
            dispatch({
                type: GET_DEFAULT_COURT_CONFIG_DATA_SUCCESS,
                payload: { courtConfig: data },
            });
        }
    } catch (error) {
        console.error('Failed to load default court config', error);
        dispatch({ type: GET_DEFAULT_COURT_CONFIG_DATA_FAILURE });
    }
};

export const getCourtConfigData = (eventId, selectedDate) => async (dispatch, getState, { mainApi }) => {
    const { courtScheduler } = getState();

    dispatch({
        type: GET_COURT_CONFIG_DATA,
        payload: { selectedDate },
    });

    let isConfigFetchFailed = false;
    try {
        const response = await getCourtSchedulerConfigForTheDay(mainApi, eventId, {
            date: moment.utc(selectedDate).format(dateFormatForDayConfig),
        });

        const { data, status } = response;
        if (status === 204) {
            isConfigFetchFailed = true;
            dispatch({ type: GET_COURT_CONFIG_DATA_FAILURE });
        } else {
            dispatch({
                type: GET_COURT_CONFIG_DATA_SUCCESS,
                payload: { courtConfig: data },
            });
        }
    } catch (error) {
        console.error('Failed to load court config for day', error);
        isConfigFetchFailed = true;
        dispatch({ type: GET_COURT_CONFIG_DATA_FAILURE });
    }

    if (isConfigFetchFailed && !courtScheduler.defaultCourtConfig) {
        await dispatch(getDefaultConfigData(eventId));
    }

    await dispatch(checkAndUpdateSaveConfigViewDisplay());
    if (!isConfigFetchFailed) {
        await dispatch(updateFacilitiesListForConfigDay());
    }
};

export const updateFacilitiesListForConfigDay = () => async (dispatch, getState) => {
    const { drawEdit, courtScheduler } = getState();
    const { locations } = drawEdit;
    const { defaultCourtConfig, selectedDateCourtConfig, selectedFacilityId } = courtScheduler;

    const configToUse = selectedDateCourtConfig || defaultCourtConfig;
    const activeFacilitiesAndCourts = getActiveFacilitiesAndCourts(locations?.data || []);
    const facilitiesToDisplay = getFacilitiesAndCourtsToDisplay(activeFacilitiesAndCourts, configToUse);

    dispatch({
        type: UPDATE_FACILITIES_BASED_ON_CONFIG,
        payload: {
            facilitiesAvailable: facilitiesToDisplay,
        },
    });

    const isSelectedFacilityInUpdatedFacilities = facilitiesToDisplay.find((fac) => fac.id === selectedFacilityId);
    if (!isSelectedFacilityInUpdatedFacilities) {
        const newSelectedFacilityId = facilitiesToDisplay?.length > 0 ? facilitiesToDisplay[0].id : '';
        await dispatch(setSelectedFacility(newSelectedFacilityId, facilitiesToDisplay));
    } else {
        await dispatch(setSelectedFacility(selectedFacilityId, facilitiesToDisplay));
    }
};

export const saveCourtConfigData = (eventId, params, shouldRefetchUnScheduleMatches = false) => async (dispatch, getState, { mainApi }) => {
    const { courtScheduler, drawEdit } = getState();
    const { drawIdToScheduleMatches, drawsList } = courtScheduler;
    const { sportTypeId } = drawEdit?.event?.data || {};

    dispatch({ type: SAVE_COURT_CONFIG_DATA });

    try {
        const response = await saveCourtSchedulerConfig(mainApi, eventId, params);

        dispatch({
            type: SAVE_COURT_CONFIG_DATA_SUCCESS,
            payload: { courtConfig: response.data },
        });

        await dispatch(getCourtConfigData(eventId, courtScheduler.selectedDate));
        if (shouldRefetchUnScheduleMatches && drawIdToScheduleMatches) {
            const {
                parentDraw,
                color,
            } = getDrawData(drawsList, drawIdToScheduleMatches);
            dispatch(getUnscheduledMatchesOfDraw(eventId, drawIdToScheduleMatches, parentDraw, color, sportTypeId));
        }
    } catch (error) {
        dispatch({ type: SAVE_COURT_CONFIG_DATA_FAILURE });
        dispatch(showErrorPopup({
            message: 'Error saving court config',
            error,
        }));
    }
};

export const updateSaveConfigViewDisplay = (show = false) => async (dispatch) => {
    dispatch({
        type: UPDATE_SHOW_CONFIG_MODAL,
        payload: { showConfigModal: show },
    });
};

export const checkAndUpdateSaveConfigViewDisplay = () => async (dispatch, getState) => {
    const { courtScheduler } = getState();
    const { selectedDate, selectedDateCourtConfig } = courtScheduler;

    const show = selectedDate && !selectedDateCourtConfig;
    dispatch(updateSaveConfigViewDisplay(show));
};

export const setSelectedFacility = (facilityId, facilitiesAvailable = []) => async (dispatch) => {
    const selectedFacility = facilitiesAvailable.find((facility) => facility.id === facilityId);

    let courtsOfSelectedFacility = [];
    if (selectedFacility) {
        courtsOfSelectedFacility = selectedFacility.courts.map((court) => {
            return {
                ...court,
                facilityId: selectedFacility.id,
                facilityName: selectedFacility.name,
                facilityAbbreviation: selectedFacility.abbreviation,
            };
        });
    }

    dispatch({
        type: UPDATE_SELECTED_FACILITY,
        payload: {
            facilityId,
            courtsOfSelectedFacility,
        },
    });

    await dispatch(getScheduledMatchesForDateAndFacility());
};

export const setSelectedDrawWithMatch = (drawId) => async (dispatch) => {
    dispatch({
        type: UPDATE_SELECTED_DRAW_WITH_MATCHES,
        payload: {
            selectedDrawWithMatches: drawId,
        },
    });
};

export const updateDrawSettings = (eventId, drawId, payload, sportTypeId, scheduleTypeId) => async (dispatch, getState, { mainApi }) => {
    const { courtScheduler } = getState();
    const {
        eliminationTypeId,
        parentDrawEliminationTypeId,
        ...others
    } = payload;

    try {
        let response;
        const isProDraw = parseInt(eliminationTypeId) === drawEliminationTypeIds.PRO_DRAWS;

        if (isProDraw) {
            const updatedPayload = {
                ...others,
                eliminationTypeId: parentDrawEliminationTypeId,
                id: drawId,
            };
            response = await updateProDraw(mainApi, {
                eventId,
                payload: updatedPayload,
            });
        } else {
            response = await updateDraw(mainApi, { eventId, drawId, payload });
        }

        if (response.data.error) {
            dispatch(showErrorPopup({
                message: 'Error updating draw settings',
                error: response.data.error,
            }));
        } else {
            await dispatch(getDraws(eventId));
            if (scheduleTypeId === drawScheduleTypeIds.NEXT_AVAILABLE) {
                await dispatch(nextAvailableActions.getDrawsConnectedToFacilityAndDayConfig(eventId));
            } else if (courtScheduler.drawIdToScheduleMatches) {
                const {
                    color,
                    parentDraw,
                } = getDrawData(courtScheduler.drawsList, courtScheduler.drawIdToScheduleMatches);

                await dispatch(getUnscheduledMatchesOfDraw(eventId, courtScheduler.drawIdToScheduleMatches, parentDraw, color, sportTypeId));
            } else {
                // Do nothing added to resolve sonar warning
            }
        }
    } catch (error) {
        dispatch(showErrorPopup({
            message: 'Error updating draw settings',
            error: getApiErrorMessage(error),
        }));
    }
};

export const updateDrawsListForCourtScheduler = (draws, eventSchedule, eventId) => async (dispatch, getState, { mainApi }) => {
    const { coreUI } = getState();

    const { drawCategoryTypes = [] } = coreUI.types?.tms?.draw || {};
    const drawCategoryTypesDict = updateCategoryTypesName(createTypeByIdDict(drawCategoryTypes));

    dispatch({ type: 'SPLASH_SHOW_LOADING' });

    let matchCountsDict = {};
    try {
        const matchCountsRes = await getDrawMatchCounts(mainApi, eventId);
        const matchCounts = matchCountsRes.data?.drawMatchCounts || [];
        matchCountsDict = buildMatchCountsDict(matchCounts);
    } catch (error) {
        console.error('Failed to load draw match counts', error);
        // On failure, matchCountsDict stays empty — draws render without counts
    }

    dispatch({ type: 'SPLASH_HIDE' });

    resetColorIndex();
    const drawsListWithColor = draws.data?.map((parentDraw) => {
        let childDraws;
        if (parentDraw.childDraws?.length > 0) {
            childDraws = [];
            const shouldDisplayMainDrawAsChild = shouldDisplayMainDrawAsChildDraw(parentDraw);
            if (shouldDisplayMainDrawAsChild) {
                childDraws.push({
                    ...parentDraw,
                    drawColor: generateRandomColor(),
                    customDrawId: `child-${parentDraw.id}`,
                    childDraws: [],
                    name: getDrawName(parentDraw, parentDraw, drawCategoryTypesDict),
                    canEditDraw: false,
                    totalMatchCount: matchCountsDict[parentDraw.id]?.totalMatchCount ?? null,
                    scheduledMatchCount: matchCountsDict[parentDraw.id]?.scheduledMatchCount ?? null,
                });
            }
            const updatedChildDraws = parentDraw.childDraws?.filter(shouldDisplayDraws)
                .map((childDraw) => {
                    return {
                        ...childDraw,
                        drawColor: generateRandomColor(),
                        customDrawId: `child-${childDraw.id}`,
                        childDraws: [],
                        name: getDrawName(childDraw, parentDraw, drawCategoryTypesDict),
                        totalMatchCount: matchCountsDict[childDraw.id]?.totalMatchCount ?? null,
                        scheduledMatchCount: matchCountsDict[childDraw.id]?.scheduledMatchCount ?? null,
                    };
                });
            childDraws = childDraws.concat(updatedChildDraws);
        }

        return {
            ...parentDraw,
            drawColor: childDraws?.length > 0 ? null : generateRandomColor(),
            childDraws,
            customDrawId: `parent-${parentDraw.id}`,
            formattedStartDate: parentDraw.startDateTimeUtc &&
                moment.utc(parentDraw.startDateTimeUtc).tz(eventSchedule.timeZone?.value).format(dateFormat),
            totalMatchCount: matchCountsDict[parentDraw.id]?.totalMatchCount ?? null,
            scheduledMatchCount: matchCountsDict[parentDraw.id]?.scheduledMatchCount ?? null,
        };
    });

    dispatch({
        type: UPDATE_DRAWS_LIST,
        payload: {
            drawsList: drawsListWithColor,
        },
    });
};

export const getUnscheduledMatchesOfDraw = (eventId, drawId, parentDraw, drawColor, sportTypeId) => async (dispatch, getState, { mainApi }) => {
    dispatch({ type: GET_UNSCHEDULED_DRAW_MATCHES, payload: { drawId } });

    const { coreUI, courtScheduler } = getState();
    const { drawsList } = courtScheduler;
    const { drawCategoryTypes = [] } = coreUI.types?.tms?.draw || {};
    const drawCategoryTypesDict = updateCategoryTypesName(createTypeByIdDict(drawCategoryTypes));

    try {
        const unScheduledMatchesResponse = await getUnscheduledMatchesForDrawV3(mainApi, eventId, drawId);

        if (unScheduledMatchesResponse.data.error) {
            dispatch({ type: GET_UNSCHEDULED_DRAW_MATCHES_FAILURE });
        } else {
            const unScheduledMatches = (unScheduledMatchesResponse.data.matches || [])
                .map((match) => getUnScheduledMatchFromDrawMatch(
                    match,
                    sportTypeId,
                    drawCategoryTypesDict,
                    drawsList,
                ));

            dispatch({
                type: GET_UNSCHEDULED_DRAW_MATCHES_SUCCESS,
                payload: {
                    unScheduledMatches,
                },
            });
        }
    } catch (error) {
        dispatch({ type: GET_UNSCHEDULED_DRAW_MATCHES_FAILURE });
        if (error.response?.status === 403) {
            dispatch(showErrorPopup({
                message: 'You do not have permission to view this draw',
                error: getApiErrorMessage(error),
            }));
        }
    }
};

export const startDraggingOfMatch = (match) => async (dispatch) => {
    dispatch({
        type: UPDATE_DRAGGING_MATCH,
        payload: {
            draggableMatch: match,
        },
    });
};

export const showTimeConflictsError = (hasTimeConflicts, message = null, title = null) => async (dispatch) => {
    dispatch({
        type: UPDATE_HAS_TIME_CONFLICTS,
        payload: {
            hasTimeConflicts,
            message,
            title,
        },
    });
};

export const scheduleMatch = (start, courtId) => async (dispatch, getState) => {
    const { courtScheduler, drawEdit } = getState();

    if (courtScheduler.isSaving) {
        return;
    }

    const { eventSchedule, id: eventId } = drawEdit?.event?.data || {};
    const { scheduledMatches, draggableMatch, selectedFacilityId, selectedDateCourtConfig, drawsList } = courtScheduler;

    if (!draggableMatch) {
        return;
    }

    const matchToSchedule = createScheduledMatchObjectFromUnscheduledMatch(
        draggableMatch,
        start,
        courtId,
        selectedFacilityId,
        eventSchedule,
    );

    const timeConflicts = checkMatchTimeConflicts(matchToSchedule, selectedDateCourtConfig, scheduledMatches, drawsList, eventSchedule);

    if (timeConflicts.isConflicting) {
        dispatch(showTimeConflictsError(timeConflicts.isConflicting, timeConflicts.message, CONFLICT_TITLE_SCHEDULE));

        // Clear dragging match state
        dispatch(startDraggingOfMatch(null));
        return;
    }

    const clampedMatchToSchedule = clampMatchEndTimeToDayEnd(matchToSchedule, selectedDateCourtConfig);
    scheduledMatches.push(clampedMatchToSchedule);

    // Update time configs for all matches in courts that were changed
    const courtIdsToResetTimePreferences = [courtId];
    await dispatch(updatedTimePreferencesForScheduledMatchesAndSave(scheduledMatches, courtIdsToResetTimePreferences));

    // Clear scheduled match from unscheduled matches list
    await dispatch(removeScheduleMatchFromUnScheduledMatchesList(draggableMatch));

    // Clear dragging match state
    await dispatch(startDraggingOfMatch(null));

    await dispatch(saveScheduledMatch(eventId, clampedMatchToSchedule, true));
};

export const rescheduleMatch = (start, end, newCourtId, matchToReschedule) => async (dispatch, getState) => {
    const { courtScheduler, drawEdit } = getState();

    if (courtScheduler.isSaving) {
        return;
    }

    const { eventSchedule, id: eventId } = drawEdit?.event?.data || {};
    const { scheduledMatches, selectedFacilityId, selectedDateCourtConfig, drawsList } = courtScheduler;

    // Remove match scheduled from List
    const updatedScheduledMatches = scheduledMatches.filter((match) => {
        return match.id !== matchToReschedule.id;
    });

    const matchToSchedule = updateScheduledMatchObjectTimes(
        matchToReschedule,
        start,
        end,
        newCourtId,
        selectedFacilityId,
        eventSchedule,
    );

    const timeConflicts = checkMatchTimeConflicts(matchToSchedule, selectedDateCourtConfig, updatedScheduledMatches, drawsList, eventSchedule);
    if (timeConflicts.isConflicting) {
        dispatch(showTimeConflictsError(timeConflicts.isConflicting, timeConflicts.message, CONFLICT_TITLE_RESCHEDULE));
        return;
    }

    const clampedMatchToSchedule = clampMatchEndTimeToDayEnd(matchToSchedule, selectedDateCourtConfig);

    // Add match with new times and courts to the list
    updatedScheduledMatches.push(clampedMatchToSchedule);

    // Update time configs for all matches in courts that were changed
    const courtIdsToResetTimePreferences = [newCourtId];
    if (matchToReschedule.courtId !== newCourtId) {
        courtIdsToResetTimePreferences.push(matchToReschedule.courtId);
    }
    await dispatch(updatedTimePreferencesForScheduledMatchesAndSave(updatedScheduledMatches, courtIdsToResetTimePreferences));

    await dispatch(saveScheduledMatch(eventId, clampedMatchToSchedule, false));
};

export const removeScheduleMatchFromUnScheduledMatchesList = (match) => async (dispatch, getState) => {
    const { courtScheduler } = getState();
    const { unScheduledMatches } = courtScheduler;

    const updatedUnScheduledMatches = unScheduledMatches.filter((unScheduleMatch) => {
        return unScheduleMatch.id !== match.id;
    });

    dispatch({
        type: GET_UNSCHEDULED_DRAW_MATCHES_SUCCESS,
        payload: {
            unScheduledMatches: updatedUnScheduledMatches,
        },
    });
};

export const updatedTimePreferencesForScheduledMatchesAndSave = (scheduledMatches, courtIdsToResetTimePreferences) => async (dispatch) => {
    const scheduledMatchesToSave = courtIdsToResetTimePreferences.reduce((updatedScheduledMatches, courtId) => {
        const courtScheduledMatches = updatedScheduledMatches.filter((sMatch) => sMatch.courtId === courtId);
        const otherCourtScheduledMatches = updatedScheduledMatches.filter((sMatch) => sMatch.courtId !== courtId);

        const updatedCourtScheduledMatches = courtScheduledMatches
            .map((csMatch) => addOrUpdateTimePreferenceToScheduledMatch(csMatch, updatedScheduledMatches))
            .sort((match1, match2) => {
                const match1StartTime = moment.utc(match1.startTime);
                const match2StartTime = moment.utc(match2.startTime);
                return match1StartTime.isBefore(match2StartTime, 'second', '()');
            })
            .map((csMatch, index) => {
                return {
                    ...csMatch,
                    isPublished: csMatch.isPublished && (csMatch.sequenceNumber === index + 1),
                    sequenceNumber: index + 1,
                };
            });

        return [
            ...otherCourtScheduledMatches,
            ...updatedCourtScheduledMatches,
        ];
    }, scheduledMatches);

    dispatch({
        type: UPDATE_SCHEDULED_MATCHES,
        payload: {
            scheduledMatches: scheduledMatchesToSave,
        },
    });

    await dispatch(updateDrawsWithScheduledMatches());
};

export const revertMatchAfterSaveFailed = (isFromUnScheduledMatches = false) => async (dispatch, getState) => {
    const { drawEdit, courtScheduler } = getState();
    const { drawIdToScheduleMatches, drawsList } = courtScheduler;
    const { id: eventId, sportTypeId } = drawEdit?.event?.data || {};

    await dispatch(getScheduledMatchesForDateAndFacility());
    if (isFromUnScheduledMatches && drawIdToScheduleMatches) {
        const {
            parentDraw,
            color,
        } = getDrawData(drawsList, drawIdToScheduleMatches);
        await dispatch(getUnscheduledMatchesOfDraw(eventId, drawIdToScheduleMatches, parentDraw, color, sportTypeId));
    }
};

export const fetchDrawMatchCounts = (eventId) => async (dispatch, getState, { mainApi }) => {
    try {
        const matchCountsRes = await getDrawMatchCounts(mainApi, eventId);
        const matchCounts = matchCountsRes.data?.drawMatchCounts || [];
        const matchCountsDict = buildMatchCountsDict(matchCounts);

        const { courtScheduler } = getState();
        const updatedDrawsList = courtScheduler.drawsList.map((parentDraw) => {
            const updatedParent = {
                ...parentDraw,
                childDraws: parentDraw.childDraws || [],
                totalMatchCount: matchCountsDict[parentDraw.id]?.totalMatchCount ?? parentDraw.totalMatchCount,
                scheduledMatchCount: matchCountsDict[parentDraw.id]?.scheduledMatchCount ?? parentDraw.scheduledMatchCount,
            };
            if (parentDraw.childDraws?.length > 0) {
                updatedParent.childDraws = parentDraw.childDraws.map((childDraw) => ({
                    ...childDraw,
                    totalMatchCount: matchCountsDict[childDraw.id]?.totalMatchCount ?? childDraw.totalMatchCount,
                    scheduledMatchCount: matchCountsDict[childDraw.id]?.scheduledMatchCount ?? childDraw.scheduledMatchCount,
                }));
            }
            return updatedParent;
        });

        dispatch({
            type: UPDATE_DRAWS_LIST,
            payload: { drawsList: updatedDrawsList },
        });
    } catch (error) {
        console.error('Failed to update draw match counts after adjust', error);
        // Counts remain as-is on failure
    }
};

export const saveScheduledMatch = (eventId, matchToSchedule, isFromUnScheduledMatches = false) => async (dispatch, getState, { mainApi }) => {
    const { drawEdit, courtScheduler } = getState();
    const { eventSchedule } = drawEdit?.event?.data || {};

    const { matchesSaved = [] } = courtScheduler;

    dispatch({ type: SAVE_SCHEDULE_MATCH });

    // Optimistic increment — only when the match is entering the schedule
    // from the unscheduled list. A reschedule (grid → grid) keeps the same
    // drawId and must not bump counts.
    if (isFromUnScheduledMatches && matchToSchedule.drawId) {
        dispatch({
            type: UPDATE_DRAW_MATCH_COUNT,
            payload: { drawId: matchToSchedule.drawId, delta: 1 },
        });
    }

    try {
        const scheduleMatchResponse = await scheduleMatchForEvent(mainApi, eventId, {
            startTime: moment.utc(matchToSchedule.startTime)
                .tz(eventSchedule?.timeZone?.value).toISOString(),
            matchId: matchToSchedule.id,
            courtId: matchToSchedule.courtId,
            dayConfigId: courtScheduler.selectedDateCourtConfig.id,
        });

        if (scheduleMatchResponse.status === 204) {
            dispatch({
                type: SAVE_SCHEDULE_MATCH_FAILURE,
            });
            dispatch(showErrorPopup({
                error: 'Error scheduling the match',
            }));
            await dispatch(revertMatchAfterSaveFailed(isFromUnScheduledMatches));
            await dispatch(fetchDrawMatchCounts(eventId));
        } else {
            if (!matchesSaved.includes(matchToSchedule.id)) {
                matchesSaved.push(matchToSchedule.id);
            }

            dispatch({
                type: SAVE_SCHEDULE_MATCH_SUCCESS,
                payload: {
                    matchesSaved,
                },
            });
        }
    } catch (error) {
        dispatch({
            type: SAVE_SCHEDULE_MATCH_FAILURE,
        });
        dispatch(showErrorPopup({
            message: 'Error scheduling the match',
            error: getApiErrorMessage(error),
        }));
        await dispatch(revertMatchAfterSaveFailed(isFromUnScheduledMatches));
        await dispatch(fetchDrawMatchCounts(eventId));
    }
};

export const getScheduledMatchesForDateAndFacility = () => async (dispatch, getState, { mainApi }) => {
    const { drawEdit, courtScheduler, coreUI } = getState();
    const { selectedFacilityId, selectedDateCourtConfig } = courtScheduler;
    const { id, sportTypeId } = drawEdit?.event?.data || {};

    const { drawCategoryTypes = [] } = coreUI.types?.tms?.draw || {};
    const drawCategoryTypesDict = updateCategoryTypesName(createTypeByIdDict(drawCategoryTypes));

    dispatch({ type: GET_SCHEDULED_MATCHES });

    try {
        const response = await getScheduledMatchesForEventV3(mainApi, id, {
            facilityId: selectedFacilityId,
            dayConfigId: selectedDateCourtConfig.id,
        });

        const { data, status } = response;
        if (status === 204) {
            dispatch({ type: GET_SCHEDULED_MATCHES_FAILURE });
        } else {
            // Re-read drawsList after the await so an in-flight UPDATE_DRAWS_LIST
            // dispatch is picked up — otherwise the transform silently builds
            // matches without draw details.
            const { drawsList } = getState().courtScheduler;
            // BE recomputes endTime from start + allottedMatchTime, so the day-end
            // clamp applied at save time doesn't survive the round-trip. Re-apply
            // here so the calendar grid (bounded to a single day) keeps grace-window
            // matches visible on reload.
            const scheduledMatches = data.matches
                .map(match => getScheduledMatchObjectFromDraftOpp(match, drawCategoryTypesDict, drawsList, sportTypeId))
                .map(match => clampMatchEndTimeToDayEnd(match, selectedDateCourtConfig));
            dispatch({
                type: GET_SCHEDULED_MATCHES_SUCCESS,
                payload: {
                    scheduledMatches,
                    unPublishedMatchCount: data.unPublishedMatchCount,
                    publishedDeletedDraftMatchCount: data.publishedDeletedDraftMatchCount,
                    lastPublishedDate: data.lastPublishedDate,
                },
            });
            await dispatch(updateDrawsWithScheduledMatches());
        }
    } catch (error) {
        dispatch({ type: GET_SCHEDULED_MATCHES_FAILURE });
        if (error.response?.status === 403) {
            dispatch(showErrorPopup({
                message: 'You do not have permission to view this schedule',
                error: getApiErrorMessage(error),
            }));
        }
    }
};

export const publishScheduledMatches = () => async (dispatch, getState, { mainApi }) => {
    const { drawEdit, courtScheduler } = getState();
    const { selectedFacilityId, selectedDateCourtConfig } = courtScheduler;
    const { id: eventId } = drawEdit?.event?.data || {};

    dispatch({ type: PUBLISH_SCHEDULED_MATCHES });

    try {
        const response = await publishScheduleForEvent(mainApi, eventId, {
            facilityId: selectedFacilityId,
            dayConfigId: selectedDateCourtConfig.id,
        });

        if (response?.data.error) {
            dispatch({ type: PUBLISH_SCHEDULED_MATCHES_FAILURE });
            dispatch(showErrorPopup({
                message: 'Error publishing schedule',
                error: response.data.error,
            }));
        } else {
            dispatch({ type: PUBLISH_SCHEDULED_MATCHES_SUCCESS });

            await dispatch(getScheduledMatchesForDateAndFacility());
            // Refetch order of play data to refresh published match data in draws
            await dispatch(getOrderOfPlay(eventId));
            await dispatch(getOrderOfPlayMatches(eventId));
        }
    } catch (error) {
        dispatch({ type: PUBLISH_SCHEDULED_MATCHES_FAILURE });
        dispatch(showErrorPopup({
            message: 'Error publishing schedule',
            error,
        }));
    }
};

export const revertScheduledMatches = () => async (dispatch, getState, { mainApi }) => {
    const { drawEdit, courtScheduler } = getState();
    const { selectedFacilityId, selectedDateCourtConfig, drawIdToScheduleMatches, drawsList } = courtScheduler;
    const { id: eventId, sportTypeId } = drawEdit?.event?.data || {};

    dispatch({ type: REVERT_SCHEDULED_MATCHES });

    try {
        const response = await revertScheduleForEvent(mainApi, eventId, {
            facilityId: selectedFacilityId,
            dayConfigId: selectedDateCourtConfig.id,
        });

        if (response?.data.error) {
            dispatch({ type: REVERT_SCHEDULED_MATCHES_FAILURE });
            dispatch(showErrorPopup({
                message: 'Error reverting schedule',
                error: response.data.error,
            }));
            await dispatch(fetchDrawMatchCounts(eventId));
        } else {
            dispatch({ type: REVERT_SCHEDULED_MATCHES_SUCCESS });

            await dispatch(getScheduledMatchesForDateAndFacility());
            await dispatch(fetchDrawMatchCounts(eventId));
            if (drawIdToScheduleMatches) {
                const {
                    parentDraw,
                    color,
                } = getDrawData(drawsList, drawIdToScheduleMatches);
                dispatch(getUnscheduledMatchesOfDraw(eventId, drawIdToScheduleMatches, parentDraw, color, sportTypeId));
            }
        }
    } catch (error) {
        dispatch({ type: REVERT_SCHEDULED_MATCHES_FAILURE });
        dispatch(showErrorPopup({
            message: 'Error reverting schedule',
            error,
        }));
        await dispatch(fetchDrawMatchCounts(eventId));
    }
};

export const updateDrawsWithScheduledMatches = () => async (dispatch, getState) => {
    const { courtScheduler } = getState();
    const { scheduledMatches, selectedDrawWithMatches, drawsList } = courtScheduler;

    const uniqueDrawIds = [...new Set(scheduledMatches.map(match => match.parentDrawId || match.drawId))];

    const drawsWithScheduledMatches = uniqueDrawIds.map((drawId) => {
        return drawsList.find((draw) => {
            return draw.id === drawId;
        });
    });

    dispatch({
        type: UPDATE_DRAWS_WITH_SCHEDULED_MATCHES,
        payload: {
            drawsWithMatches: drawsWithScheduledMatches,
        },
    });

    if (selectedDrawWithMatches && !uniqueDrawIds.includes(selectedDrawWithMatches)) {
        dispatch(setSelectedDrawWithMatch(''));
    }
};

export const updateScheduledMatchSettings = (eventId, matchId, updatedMatch, requestBody) => async (dispatch, getState, { mainApi }) => {
    const { courtScheduler } = getState();
    const { matchesSaved = [], scheduledMatches } = courtScheduler;

    dispatch({
        type: MATCH_SETTINGS_UPDATE,
    });

    try {
        const response = await updateMatchSettings(mainApi, eventId, matchId, requestBody);
        if (response.error) {
            dispatch({
                type: MATCH_SETTINGS_UPDATE_FAILURE,
                payload: response.error,
            });
        } else {
            if (!matchesSaved.includes(matchId)) {
                matchesSaved.push(matchId);
            }

            const updatedScheduledMatches = scheduledMatches.filter((match) => match.id !== matchId);
            updatedScheduledMatches.push({
                ...updatedMatch,
            });

            dispatch({
                type: MATCH_SETTINGS_UPDATE_SUCCESS,
                payload: {
                    matchesSaved,
                    scheduledMatches: updatedScheduledMatches,
                },
            });

            await dispatch(getScheduledMatchesForDateAndFacility());
        }
    } catch (error) {
        dispatch({
            type: MATCH_SETTINGS_UPDATE_FAILURE,
            payload: error?.message,
        });
    }
};

export const toggleMatchSelection = (matchId) => async (dispatch, getState) => {
    const { courtScheduler } = getState();
    const { scheduledMatches, selectedMatchIds = [] } = courtScheduler;

    const isInScheduledMatches = scheduledMatches.find((sm) => sm.id === matchId);

    if (isInScheduledMatches) {
        const updatedMatchIds = [
            ...selectedMatchIds,
        ];
        const matchIndex = updatedMatchIds.findIndex((id) => id === matchId);
        if (matchIndex > -1) {
            updatedMatchIds.splice(matchIndex, 1);
        } else {
            updatedMatchIds.push(matchId);
        }

        dispatch({
            type: UPDATE_SELECTED_MATCH_IDS,
            payload: {
                selectedMatchIds: updatedMatchIds,
            },
        });
    }
};

export const selectAllMatches = () => async (dispatch, getState) => {
    const { courtScheduler } = getState();
    const { scheduledMatches } = courtScheduler;

    const matchIds = scheduledMatches.map((sm) => sm.id);

    dispatch({
        type: UPDATE_SELECTED_MATCH_IDS,
        payload: {
            selectedMatchIds: matchIds,
        },
    });
};

export const clearMatchSelection = () => async (dispatch) => {
    dispatch({
        type: UPDATE_SELECTED_MATCH_IDS,
        payload: {
            selectedMatchIds: [],
        },
    });
};

export const deScheduleAllSelectedMatches = () => async (dispatch, getState, { mainApi }) => {
    const { courtScheduler, drawEdit } = getState();
    const { selectedMatchIds, drawIdToScheduleMatches, drawsList, scheduledMatches } = courtScheduler;
    const { id: eventId, sportTypeId } = drawEdit?.event?.data || {};

    // Build draw-level decrement counts for optimistic update
    const drawDecrements = {};
    selectedMatchIds.forEach((matchId) => {
        const match = scheduledMatches.find((sm) => sm.id === matchId);
        if (match?.drawId) {
            drawDecrements[match.drawId] = (drawDecrements[match.drawId] || 0) + 1;
        }
    });

    dispatch({
        type: DE_SCHEDULE_MATCHES,
    });

    // Optimistic decrement
    Object.entries(drawDecrements).forEach(([drawId, count]) => {
        dispatch({
            type: UPDATE_DRAW_MATCH_COUNT,
            payload: { drawId, delta: -count },
        });
    });

    try {
        const response = await deScheduleMatches(mainApi, eventId, {
            matchIds: selectedMatchIds,
        });

        if (response.error) {
            dispatch({
                type: DE_SCHEDULE_MATCHES_FAILURE,
            });
            dispatch(showErrorPopup({
                message: 'Failed to remove matches',
                error: response.error,
            }));
            await dispatch(fetchDrawMatchCounts(eventId));
        } else {
            dispatch({
                type: DE_SCHEDULE_MATCHES_SUCCESS,
            });
            dispatch(clearMatchSelection());
            dispatch(getScheduledMatchesForDateAndFacility());
            if (drawIdToScheduleMatches) {
                const {
                    parentDraw,
                    color,
                } = getDrawData(drawsList, drawIdToScheduleMatches);
                dispatch(getUnscheduledMatchesOfDraw(eventId, drawIdToScheduleMatches, parentDraw, color, sportTypeId));
            }
        }
    } catch (error) {
        dispatch({
            type: DE_SCHEDULE_MATCHES_FAILURE,
        });
        dispatch(showErrorPopup({
            message: 'Failed to remove matches',
            error,
        }));
        await dispatch(fetchDrawMatchCounts(eventId));
    }
};

export const startAllSelectedMatches = () => async (dispatch, getState, { mainApi }) => {
    const { courtScheduler, drawEdit } = getState();
    const { selectedMatchIds } = courtScheduler;
    const { id: eventId } = drawEdit?.event?.data || {};

    dispatch({
        type: 'SPLASH_SHOW_LOADING',
        title: 'Loading',
        description: 'Please wait',
    });

    try {
        const response = await startMatches(mainApi, eventId, {
            matchIds: selectedMatchIds,
        });

        if (response.error) {
            dispatch(showErrorPopup({
                message: 'Failed to start matches',
                error: response.error,
            }));
        } else {
            dispatch(clearMatchSelection());
            dispatch(getScheduledMatchesForDateAndFacility());
        }
        dispatch({ type: 'SPLASH_HIDE' });
    } catch (error) {
        dispatch({ type: 'SPLASH_HIDE' });
        dispatch(showErrorPopup({
            message: 'Failed to start matches',
            error,
        }));
    }
};

export const pauseAllSelectedMatches = (pushBackMinutes = null) => async (dispatch, getState, { mainApi }) => {
    const { courtScheduler, drawEdit } = getState();
    const { selectedMatchIds } = courtScheduler;
    const { id: eventId } = drawEdit?.event?.data || {};

    dispatch({
        type: 'SPLASH_SHOW_LOADING',
        title: 'Loading',
        description: 'Please wait',
    });

    try {
        const response = await pauseMatches(mainApi, eventId, {
            matchIds: selectedMatchIds,
            pushBackMinutes,
        });

        if (response.error) {
            dispatch(showErrorPopup({
                message: 'Failed to pause matches',
                error: response.error,
            }));
        } else {
            dispatch(clearMatchSelection());
            dispatch(getScheduledMatchesForDateAndFacility());
        }
        dispatch({ type: 'SPLASH_HIDE' });
    } catch (error) {
        dispatch({ type: 'SPLASH_HIDE' });
        dispatch(showErrorPopup({
            message: 'Failed to pause matches',
            error,
        }));
    }
};

export const restartPausedMatch = (matchId) => async (dispatch, getState, { mainApi }) => {
    const { drawEdit } = getState();
    const { id: eventId } = drawEdit?.event?.data || {};

    dispatch({
        type: 'SPLASH_SHOW_LOADING',
        title: 'Loading',
        description: 'Please wait',
    });

    try {
        const response = await restartMatch(mainApi, eventId, matchId);

        if (response.error) {
            dispatch(showErrorPopup({
                message: 'Failed to restart match',
                error: response.error,
            }));
        } else {
            dispatch(getScheduledMatchesForDateAndFacility());
        }
        dispatch({ type: 'SPLASH_HIDE' });
    } catch (error) {
        dispatch({ type: 'SPLASH_HIDE' });
        dispatch(showErrorPopup({
            message: 'Failed to restart match',
            error,
        }));
    }
};

export const clearCourtSchedularData = () => async (dispatch) => {
    dispatch({
        type: CLEAR_COURT_SCHEDULAR_DATA,
    });
};

export const closeEditDrawer = () => async (dispatch) => {
    dispatch({
        type: CLOSE_DRAWER,
    });
};

export const adjustAllSelectedMatches = (adjustmentMinutes) => async (dispatch, getState, { mainApi }) => {
    const { courtScheduler, drawEdit } = getState();
    const { selectedMatchIds } = courtScheduler;
    const { id: eventId } = drawEdit?.event?.data || {};

    dispatch({
        type: 'SPLASH_SHOW_LOADING',
        title: 'Loading',
        description: 'Please wait',
    });

    try {
        dispatch({ type: ADJUST_SELECTED_MATCHES });
        const response = await adjustMatches(mainApi, eventId, {
            matchIds: selectedMatchIds,
            adjustmentMinutes,
        });

        if (response.error) {
            dispatch({ type: ADJUST_SELECTED_MATCHES_FAILURE });
            dispatch(showErrorPopup({
                message: 'Failed to adjust matches',
                error: response.error,
            }));
        } else {
            dispatch({ type: ADJUST_SELECTED_MATCHES_SUCCESS });
            dispatch(clearMatchSelection());
            dispatch(getScheduledMatchesForDateAndFacility());
        }
        dispatch({ type: 'SPLASH_HIDE' });
    } catch (error) {
        dispatch({ type: ADJUST_SELECTED_MATCHES_FAILURE });
        dispatch({ type: 'SPLASH_HIDE' });
        dispatch(showErrorPopup({
            message: 'Failed to adjust matches',
            error,
        }));
        dispatch(getScheduledMatchesForDateAndFacility());
    }
};

export const toggleSelectAllMatchesOnCourt = (courtId) => async (dispatch, getState) => {
    const { courtScheduler } = getState();
    const { selectedMatchIds = [], scheduledMatches = [] } = courtScheduler;

    const courtMatches = scheduledMatches.filter((match) => match.courtId === courtId);
    const isAllSelected = courtMatches.length > 0 &&
        courtMatches.every((match) => selectedMatchIds.includes(match.id));

    let updatedSelectedMatchIds;
    if (isAllSelected) {
        const courtMatchIds = new Set(courtMatches.map((match) => match.id));
        updatedSelectedMatchIds = selectedMatchIds.filter((id) => !courtMatchIds.has(id));
    } else {
        const alreadySelected = new Set(selectedMatchIds);
        const toAdd = courtMatches
            .map((match) => match.id)
            .filter((id) => !alreadySelected.has(id));
        updatedSelectedMatchIds = [...selectedMatchIds, ...toAdd];
    }

    dispatch({
        type: UPDATE_SELECTED_MATCH_IDS,
        payload: { selectedMatchIds: updatedSelectedMatchIds },
    });
};
